'use client';

import { useId } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

interface InappropriateLanguageModalProps {
  open: boolean;
  onClose: () => void;
  testIdPrefix?: string;
}

/**
 * Advisory modal shown when a composer detects inappropriate language
 * (MWBI-866). Mirrors CrisisModal's prop shape and dismiss behavior; all copy
 * is sourced from the `crisis` i18n namespace so it renders in EN and ES.
 */
export function InappropriateLanguageModal({
  open,
  onClose,
  testIdPrefix = 'inappropriate-language-modal',
}: InappropriateLanguageModalProps) {
  const { t } = useLanguage();
  // Generate the aria-labelledby target id independently of testIdPrefix.
  // The forums page renders many TopicCards, each passing the same constant
  // prefix; deriving the id from the prefix produced duplicate DOM ids when two
  // reply modals were open at once, so assistive tech resolved aria-labelledby
  // to the wrong title. useId() guarantees a unique, stable id per instance.
  // testIdPrefix stays purely for data-testid hooks (MWBI-865 follow-up).
  const titleId = useId();

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center"
      data-testid={`${testIdPrefix}-root`}
    >
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative bg-background rounded-lg shadow-xl w-full max-w-sm mx-4 p-6"
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center shrink-0">
            <AlertTriangle className="text-yellow-600" size={22} aria-hidden="true" />
          </div>
          <h3 id={titleId} className="text-base font-semibold text-foreground">
            {t.crisis.inappropriateTitle}
          </h3>
        </div>
        <p className="text-sm text-foreground-muted mb-5">{t.crisis.inappropriateLanguageWarning}</p>
        <button
          onClick={onClose}
          className="w-full px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:opacity-90 transition"
          data-testid={`${testIdPrefix}-ack-btn`}
        >
          {t.crisis.inappropriateAck}
        </button>
      </div>
    </div>
  );
}
